import React, { ComponentType, ReactNode, useContext } from 'react';
import { ListBoxSectionAria } from 'react-aria';
import { isMobile } from '@direct-frontend/platform-code';
import { genericMemo } from '@direct-frontend/stdlib';

import { cnListBoxItemSeparator, cnListBoxSection, cnListBoxSeparator, ListContext } from '../utils';
import { getTextPreset } from '../Item/utils';
import { TestProps } from '../../../internal/types/testTypes';
import type { HelpHandler } from '../../Help';
import { Separator } from '../Separator/Separator';
import { TextGenericWithSkin } from '../../TextGeneric/TextGenericWithSkin';
import { ColorlessIconProps } from '../../../icons';
import { useTheme } from '../../../lib/theme';

type SectionContentProps = ListBoxSectionAria &
    HelpHandler &
    TestProps & {
        title: ReactNode;
        children: ReactNode;
        withSeparator?: boolean;
        iconLeft?: ComponentType<ColorlessIconProps>;
    };

export const SectionContent = genericMemo(function SectionContent(props: SectionContentProps) {
    const {
        title,
        children,
        itemProps,
        groupProps,
        withSeparator,
        help,
        iconLeft: IconLeft,
        'data-testid': dataTestId = 'SectionContent',
    } = props;
    const { size } = useContext(ListContext);
    const textSize = getTextPreset();
    const { iconSize } = useTheme().sizeTokens[size];

    return (
        <>
            {withSeparator && <Separator className={cnListBoxSeparator(null)} />}
            <div {...itemProps} className={cnListBoxSection(null)} data-testid={`${dataTestId}.Section`}>
                {title && (
                    <>
                        <TextGenericWithSkin
                            role={'supplementary'}
                            textSize={textSize}
                            skinSize={size}
                            color="current"
                            className={cnListBoxSection('title')}
                            help={help}
                            addonLeft={
                                IconLeft ? (
                                    <IconLeft
                                        className={cnListBoxSection('icon-left')}
                                        role={'supplementary'}
                                        size={iconSize}
                                    />
                                ) : undefined
                            }
                        >
                            {title}
                        </TextGenericWithSkin>
                        {isMobile() && <Separator className={cnListBoxItemSeparator(null)} />}
                    </>
                )}
                <div {...groupProps} className={cnListBoxSection('options')}>
                    {children}
                </div>
            </div>
        </>
    );
});
